"use client";

import Image from "next/image";
import { useCallback, useMemo, useState } from "react";
import GalleryViewer, { type GalleryImage } from "@/components/GalleryViewer";
import type { GalleryPhoto } from "@/lib/navigation";

/**
 * Destination-page photo grid that opens the shared, React-based
 * {@link GalleryViewer} lightbox on click.
 *
 * Replaces the theme's jQuery `.popup-image` Magnific Popup: that binding is
 * set up once by main.js on the initial page load, so galleries rendered after
 * a client-side navigation were never bound and their anchors fell back to
 * navigating straight to the raw image URL (open / download). This viewer is
 * self-contained React, so it works regardless of how the page was reached.
 */
export default function DestinationGallery({
  photos,
  title = "Destination Gallery",
}: {
  photos: GalleryPhoto[];
  title?: string;
}) {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  const images = useMemo<GalleryImage[]>(
    () =>
      photos.map((p, i) => ({
        id: `${p.url}#${i}`,
        src: p.url,
        alt: p.title || "gallery image",
      })),
    [photos],
  );

  const openAt = useCallback((i: number) => {
    setIndex(i);
    setOpen(true);
  }, []);

  if (photos.length === 0) return null;

  return (
    <div className="tour-gallery-wrapper">
      <h3 className="page-title mt-50 mb-30">{title}</h3>
      <div className="row gy-4">
        {photos.map((photo, i) => (
          <div key={i} className="col-lg-4 col-md-6">
            <div className="tour-gallery-card">
              <button
                type="button"
                className="gallery-img global-img"
                onClick={() => openAt(i)}
                aria-label={`Open ${photo.title || `image ${i + 1}`} in gallery viewer`}
                style={{
                  position: "relative",
                  display: "block",
                  width: "100%",
                  padding: 0,
                  border: 0,
                  background: "none",
                  cursor: "zoom-in",
                }}
              >
                <Image
                  src={photo.url}
                  alt={photo.title || "gallery image"}
                  width={400}
                  height={300}
                  style={{
                    width: "100%",
                    height: "200px",
                    objectFit: "cover",
                  }}
                  unoptimized
                />
                <span className="icon-btn" aria-hidden="true">
                  <i className="fal fa-magnifying-glass-plus"></i>
                </span>
              </button>
            </div>
          </div>
        ))}
      </div>

      <GalleryViewer
        open={open}
        images={images}
        initialIndex={index}
        onClose={() => setOpen(false)}
        title={title}
      />
    </div>
  );
}
